import { useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { useQueryClient, type QueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import type { IConversation } from 'src/api/landlord-messages-api';
import {
    patchConversationProperty,
    reconcileLeadCollection,
} from 'src/components/live-updates-reaction/lead-cache-reconciliation';
import { handleLeadDeleted } from 'src/components/live-updates-reaction/handle-lead-deleted';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { fetchLead } from 'src/services/api/leads/use-lead';
import type { ILead } from 'src/services/api/leads/types';
import { subscribeToFrames } from 'src/services/websocket/live-updates-signal';
import { FRAME_REFETCH_DEBOUNCE_MS } from 'src/services/websocket/use-live-refetch';
import { parseLeadLifecycleFrame } from 'src/services/websocket/ws-protocol';

const refetchActiveLeadCollections = async (
    queryClient: QueryClient,
) => {
    await Promise.allSettled([
        queryClient.refetchQueries(
            [queryKeys.conversations],
            { exact: true, active: true },
        ),
        queryClient.refetchQueries(
            [queryKeys.conversationsSearch],
            { active: true },
        ),
        queryClient.refetchQueries([queryKeys.showings], { active: true }),
    ]);
};

const refetchActiveLeadDependents = async (
    queryClient: QueryClient,
    leadUuid: string,
) => {
    await Promise.allSettled([
        queryClient.refetchQueries(
            [queryKeys.conversations, 'lead', leadUuid],
            { exact: true, active: true },
        ),
        queryClient.refetchQueries(
            [queryKeys.leadStageHistory, leadUuid],
            { exact: true, active: true },
        ),
    ]);
};

export const refetchLeadCollection = async (queryClient: QueryClient) => {
    await queryClient.refetchQueries([queryKeys.leads], { exact: true });
};

export const refetchAllActiveLeadState = async (queryClient: QueryClient) => {
    // The collection refetch seeds detail entries; observed leads outside the
    // collection refresh independently when their observers run.
    await Promise.allSettled([
        queryClient.refetchQueries([queryKeys.leads], { exact: true }),
        queryClient.refetchQueries([queryKeys.leadStageHistory], { active: true }),
        queryClient.refetchQueries([queryKeys.conversations], { active: true }),
        queryClient.refetchQueries([queryKeys.conversationsSearch], { active: true }),
        queryClient.refetchQueries([queryKeys.showings], { active: true }),
    ]);
};

export const useLeadLiveUpdates = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const { addNotification } = useNotifications();
    const refreshingLeadUuidsRef = useRef(new Set<string>());
    const deletedLeadUuidsRef = useRef(new Set<string>());
    const routeActionsRef = useRef({ location, navigate, addNotification });

    useEffect(() => {
        routeActionsRef.current = { location, navigate, addNotification };
    }, [addNotification, location, navigate]);

    useEffect(() => {
        const refetchCollections = debounce(
            () => void refetchActiveLeadCollections(queryClient),
            FRAME_REFETCH_DEBOUNCE_MS,
        );

        const fetchAndReconcileLead = async (leadUuid: string) => {
            const lead = await queryClient.fetchQuery<ILead>(
                [queryKeys.leads, leadUuid],
                () => fetchLead(leadUuid),
                { staleTime: 0, retry: 1 },
            );

            if (deletedLeadUuidsRef.current.has(leadUuid)) {
                queryClient.removeQueries([queryKeys.leads, leadUuid]);
                return undefined;
            }

            queryClient.setQueryData<ILead[]>([queryKeys.leads], (cachedLeads) => (
                reconcileLeadCollection(cachedLeads, lead)
            ));
            return lead;
        };

        const refreshUpdatedLead = async (leadUuid: string) => {
            const lead = await fetchAndReconcileLead(leadUuid);
            if (!lead) {
                return;
            }

            const conversationsKey = [queryKeys.conversations];
            const cachedConversations = queryClient.getQueryData<IConversation[]>(
                conversationsKey,
            );
            if (cachedConversations) {
                queryClient.setQueryData<IConversation[]>(
                    conversationsKey,
                    patchConversationProperty(cachedConversations, lead) ?? [],
                );
            }
            const conversationKey = [queryKeys.conversations, 'lead', leadUuid];
            const cachedConversation = queryClient.getQueryData<IConversation>(
                conversationKey,
            );
            if (cachedConversation) {
                queryClient.setQueryData<IConversation>(conversationKey, {
                    ...cachedConversation,
                    property_id: lead.property_id,
                });
            }

            await refetchActiveLeadDependents(queryClient, leadUuid);
            refetchCollections();
        };

        const refreshLeadFromFrame = (
            leadUuid: string,
            refresh: (uuid: string) => Promise<unknown>,
        ) => {
            if (refreshingLeadUuidsRef.current.has(leadUuid)) {
                return;
            }

            refreshingLeadUuidsRef.current.add(leadUuid);
            void refresh(leadUuid)
                .catch(() => undefined)
                .finally(() => refreshingLeadUuidsRef.current.delete(leadUuid));
        };

        const unsubscribe = subscribeToFrames((frame) => {
            const lifecycleFrame = parseLeadLifecycleFrame(frame);
            if (!lifecycleFrame) {
                return;
            }

            const leadUuid = lifecycleFrame.lead_uuid;
            if (lifecycleFrame.type === 'lead.deleted') {
                if (deletedLeadUuidsRef.current.has(leadUuid)) {
                    return;
                }
                deletedLeadUuidsRef.current.add(leadUuid);
                handleLeadDeleted({
                    queryClient,
                    leadUuid,
                    routeActions: routeActionsRef.current,
                    refetchCollections,
                });
                return;
            }

            if (lifecycleFrame.type === 'lead.created') {
                deletedLeadUuidsRef.current.delete(leadUuid);
                refreshLeadFromFrame(leadUuid, fetchAndReconcileLead);
            } else if (deletedLeadUuidsRef.current.has(leadUuid)) {
                return;
            } else {
                refreshLeadFromFrame(leadUuid, refreshUpdatedLead);
            }
        });

        return () => {
            unsubscribe();
            refetchCollections.cancel();
        };
    }, [queryClient]);
};
